import { useState, useEffect, useCallback } from "react";
import Layout from "../../../components/Layout";
import { StatusBadge } from "../../../components/StatusBadge";
import { HiGift, HiSearch, HiCheck, HiRefresh, HiDownload, HiExclamation } from "react-icons/hi";
import { CustomButton, CustomInput } from "../../../components/FormFields";
import { CustomTable, CustomTableHead, CustomTableBody, CustomTableHeadCell, CustomTableRow, CustomTableCell } from "../../../components/CustomTable";
import { fetchActiveEmployees } from "../../../services/employeeService";
import { fetchAllStructures } from "../../../services/salaryStructureService";
import {
  fetchBonuses, seedBonusSheet, clearBonusDraft, updateBonus, finaliseBonuses,
} from "../../../services/incrementBonusService";
import { downloadCSV } from "../../../services/reportService";

const money = (v) => Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
const monthLabel = (m) => {
  if (!m) return "";
  const [y, mm] = String(m).split("-");
  return new Date(Number(y), Number(mm) - 1, 1).toLocaleString(undefined, { month: "long", year: "numeric" });
};
// Bonus is normally paid in December.
const defaultMonth = () => `${new Date().getFullYear()}-12`;

export default function BonusSheet() {
  const [month, setMonth]     = useState(defaultMonth);
  const [rows, setRows]       = useState([]);
  const [search, setSearch]   = useState("");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy]       = useState("");
  const [error, setError]     = useState("");
  const [savingId, setSaving] = useState(null);

  const finalised = rows.length > 0 && rows.every((r) => r.status === "Finalised");

  const load = useCallback(async (m) => {
    setLoading(true); setError("");
    try {
      const d = await fetchBonuses(m);
      setRows(Array.isArray(d) ? d : []);
    } catch (e) {
      console.error(e); setError("Could not load the bonus sheet.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(month); }, [month, load]);

  const handleGenerate = async () => {
    setBusy("Generating…"); setError("");
    try {
      const [emps, structures] = await Promise.all([fetchActiveEmployees(), fetchAllStructures()]);
      // budgetary allowance per employee — it forms part of the bonus
      const budgetary = {};
      (Array.isArray(structures) ? structures : []).forEach((s) => {
        if (s.type === "Allowance" && (s.componentName || "").toLowerCase().includes("budgetary")) {
          const k = String(s.emp_id);
          budgetary[k] = (budgetary[k] || 0) + Number(s.value || 0);
        }
      });

      const list = (Array.isArray(emps) ? emps : []).map((e) => {
        const basic = Number(e.basicSalary) || 0;
        const bud   = budgetary[String(e.emp_id)] || 0;
        return {
          emp_id: e.emp_id,
          empName: `${e.initial ?? ""} ${e.firstName ?? ""} ${e.surName ?? ""}`.replace(/\s+/g, " ").trim(),
          category: e.category ?? "",
          basicSalary: basic,
          budgetaryAllowance: bud,
          bonusAmount: basic + bud,   // one month's salary
        };
      });

      await clearBonusDraft(month);
      await seedBonusSheet(month, list);
      await load(month);
    } catch (e) {
      console.error(e); setError("Could not generate the sheet.");
    } finally { setBusy(""); }
  };

  const editAmount = (id, value) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, bonusAmount: value } : r)));

  const commitAmount = async (row) => {
    if (row.status !== "Draft") return;
    setSaving(row.id);
    try { await updateBonus(row.id, Number(row.bonusAmount) || 0); }
    catch (e) { console.error(e); setError("Could not save that amount."); }
    finally { setSaving(null); }
  };

  const handleFinalise = async () => {
    if (!window.confirm(`Finalise the bonus sheet for ${monthLabel(month)}? Amounts can no longer be edited afterwards.`)) return;
    setBusy("Finalising…"); setError("");
    try { await finaliseBonuses(month); await load(month); }
    catch (e) { console.error(e); setError("Could not finalise the sheet."); }
    finally { setBusy(""); }
  };

  const filtered = rows.filter((r) => {
    const q = search.toLowerCase();
    return !q || (r.empName || "").toLowerCase().includes(q) || String(r.emp_id).includes(q);
  });
  const totalBonus = filtered.reduce((a, r) => a + (Number(r.bonusAmount) || 0), 0);

  return (
    <Layout>
      <div className="flex flex-col gap-6">

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Bonus Sheet</h1>
            <p className="mt-1 text-sm text-gray-500">One month's salary — Basic + Budgetary Allowance, normally paid in December</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200" />
            <StatusBadge color={finalised ? "success" : "warning"} className="px-3 py-1.5 text-sm font-semibold">
              {finalised ? "Finalised" : "Draft"}
            </StatusBadge>
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
            <HiExclamation className="h-5 w-5 flex-shrink-0" /> {error}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <CustomButton color="purple" disabled={!!busy || finalised} onClick={handleGenerate}>
            <HiRefresh className="mr-2 h-4 w-4" /> {rows.length ? "Regenerate Sheet" : "Generate Sheet"}
          </CustomButton>
          {!finalised && rows.length > 0 && (
            <CustomButton color="success" disabled={!!busy} onClick={handleFinalise}>
              <HiCheck className="mr-2 h-4 w-4" /> Finalise Sheet
            </CustomButton>
          )}
          <CustomButton color="gray" outline disabled={rows.length === 0}
            onClick={() => downloadCSV(`bonus-${month}.csv`, rows, [
              { key: "emp_id", label: "Emp ID" }, { key: "empName", label: "Employee" },
              { key: "category", label: "Category" }, { key: "basicSalary", label: "Basic" },
              { key: "budgetaryAllowance", label: "Budgetary" }, { key: "bonusAmount", label: "Bonus" },
            ])}>
            <HiDownload className="mr-2 h-4 w-4" /> Export CSV
          </CustomButton>
          <CustomInput icon={HiSearch} placeholder="Search name or ID..." sizing="sm" className="w-56"
            value={search} onChange={(e) => setSearch(e.target.value)} />
          {busy && <span className="text-sm text-gray-500">{busy}</span>}
        </div>

        <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="overflow-x-auto">
            <CustomTable hoverable>
              <CustomTableHead>
                <CustomTableRow>
                  <CustomTableHeadCell>Employee</CustomTableHeadCell>
                  <CustomTableHeadCell>Category</CustomTableHeadCell>
                  <CustomTableHeadCell>Basic Salary</CustomTableHeadCell>
                  <CustomTableHeadCell>Budgetary Allow.</CustomTableHeadCell>
                  <CustomTableHeadCell className="text-amber-600">Bonus Amount ✎</CustomTableHeadCell>
                  <CustomTableHeadCell>Status</CustomTableHeadCell>
                </CustomTableRow>
              </CustomTableHead>
              <CustomTableBody className="divide-y divide-gray-100 dark:divide-gray-700">
                {loading ? (
                  <CustomTableRow><CustomTableCell colSpan={6} className="py-10 text-center text-sm text-gray-400">Loading…</CustomTableCell></CustomTableRow>
                ) : filtered.length === 0 ? (
                  <CustomTableRow><CustomTableCell colSpan={6} className="py-10 text-center text-sm text-gray-400">
                    No bonus sheet for {monthLabel(month)} yet — click <span className="font-semibold">Generate Sheet</span>.
                  </CustomTableCell></CustomTableRow>
                ) : filtered.map((r) => (
                  <CustomTableRow key={r.id} className="hover:bg-purple-50 dark:hover:bg-gray-700">
                    <CustomTableCell>
                      <p className="whitespace-nowrap text-sm font-medium text-gray-800 dark:text-gray-200">{r.empName}</p>
                      <p className="font-mono text-xs text-purple-500">{r.emp_id}</p>
                    </CustomTableCell>
                    <CustomTableCell className="whitespace-nowrap text-xs text-gray-500">{r.category || "—"}</CustomTableCell>
                    <CustomTableCell className="text-sm text-gray-700 dark:text-gray-300">{money(r.basicSalary)}</CustomTableCell>
                    <CustomTableCell className="text-sm text-gray-700 dark:text-gray-300">{money(r.budgetaryAllowance)}</CustomTableCell>
                    <CustomTableCell>
                      {r.status === "Draft" ? (
                        <input type="number" step="0.01" min="0" disabled={savingId === r.id}
                          value={r.bonusAmount ?? 0}
                          onChange={(e) => editAmount(r.id, e.target.value)}
                          onBlur={() => commitAmount(r)}
                          onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                          className="w-32 rounded border border-gray-300 bg-amber-50 px-2 py-1 text-sm font-semibold text-gray-800 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100" />
                      ) : (
                        <span className="text-sm font-bold text-green-600">{money(r.bonusAmount)}</span>
                      )}
                    </CustomTableCell>
                    <CustomTableCell>
                      <StatusBadge color={r.status === "Finalised" ? "success" : "warning"} className="w-fit text-xs">{r.status}</StatusBadge>
                    </CustomTableCell>
                  </CustomTableRow>
                ))}
              </CustomTableBody>
            </CustomTable>
          </div>

          {filtered.length > 0 && (
            <div className="grid grid-cols-2 gap-4 border-t border-gray-200 bg-gray-50 px-5 py-4 dark:border-gray-700 dark:bg-gray-900/50">
              <div>
                <p className="text-xs text-gray-500">Employees</p>
                <p className="text-lg font-bold text-gray-700 dark:text-gray-200">{filtered.length}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Total Bonus Payable</p>
                <p className="text-lg font-bold text-green-600">Rs. {money(totalBonus)}</p>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-start gap-2 rounded-lg bg-gray-50 px-4 py-3 text-xs text-gray-500 dark:bg-gray-800 dark:text-gray-400">
          <HiGift className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>
            Amounts default to Basic + Budgetary Allowance and can be adjusted per employee before finalising.
            The bonus is recorded on this sheet only — it is not added to the monthly payroll run.
          </span>
        </div>
      </div>
    </Layout>
  );
}
